// DOM overlay for the Lobby scene — three-column layout:
//
//   [ left: top 10 all-time ] [ center: WHAT IS + !start prompt ] [ right: ranks 11+ ping-pong ]
//
// Shown whenever the FSM is in LOBBY (or before we've heard from the backend).
// Hidden as soon as a round begins. Rendered outside the Three.js canvas so
// OBS compositing stays simple. The leaderboard panels are hoisted into a
// dedicated component — see ui/lobby-leaderboard.ts.

import type { LbRow } from '@sg/shared';
import { fitText } from '../ui/fit-text.js';
import {
  createLobbyLeaderboardTop,
  createLobbyLeaderboardTail,
  type LobbyLeaderboardHandle,
} from '../ui/lobby-leaderboard.js';
import { createLobbyHowTo, type LobbyHowToHandle } from '../ui/lobby-howto.js';

export interface LobbyHandle {
  el: HTMLDivElement;
  setVisible: (visible: boolean) => void;
  setPrefix: (prefix: string) => void;
  setLeaderboard: (rows: LbRow[]) => void;
  dispose: () => void;
}

// Theme-driven palette via CSS custom properties. See ui/theme.ts — fallbacks
// keep the pre-theme render identical for clients that never hear from a
// snapshot. The glow keyframes still use rgba() literals because theme var
// interpolation inside rgba() is tricky; the literal closely matches the
// default primary/secondary and is a cosmetic second-order effect.
const STYLES = `
  @keyframes sg-lobby-glow {
    0%, 100% { text-shadow: 0 4px 24px rgba(6,12,233,0.55), 0 0 30px rgba(255,196,61,0.3),  0 0 60px rgba(61,239,255,0.0);  color: var(--sg-primary, #FFC43D); }
    50%      { text-shadow: 0 4px 24px rgba(6,12,233,0.55), 0 0 90px rgba(255,196,61,0.95), 0 0 30px rgba(61,239,255,0.45); color: var(--sg-primary, #FFD466); }
  }
  @keyframes sg-lobby-bounce {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
  }
  .sg-lobby-wrap {
    position: absolute;
    inset: 0;
    display: grid;
    /* 27% + 1.5% + 43% + 1.5% + 27% = 100% — previously columns summed to
       103% which clipped the right panel by ~3% of the inner width at OBS
       resolutions (1920x1080, 1280x720). See commit history for bug repro.
       Padding is fixed pixels against the 1920x1080 stage (was 2.5vh 2vw
       = 27px 38px at 1920x1080) — the DOM stage transform handles scaling. */
    grid-template-columns: 27% 43% 27%;
    gap: 1.5%;
    padding: 27px 38px;
    box-sizing: border-box;
    pointer-events: none;
    color: #fff;
    z-index: 40;
  }
  .sg-lobby-col-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    text-align: center;
  }
  .sg-lobby-col-left,
  .sg-lobby-col-right {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  /* Stack the two left-column panels (top-10 leaderboard above, how-to-play
     below) so each sizes to its own content and the three vertical gaps —
     top-edge/panel1, panel1/panel2, panel2/bottom-edge — are equal.
     space-evenly produces the equal gaps; align-items stretch keeps each
     panel full-column-width. Children use flex 0 0 auto so they render at
     intrinsic height (header + body) with no internal empty padding. */
  .sg-lobby-col-left {
    justify-content: space-evenly;
    align-items: stretch;
  }
  .sg-lobby-col-left > :nth-child(1),
  .sg-lobby-col-left > :nth-child(2) {
    flex: 0 0 auto;
  }
  .sg-lobby-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 6.5rem;
    letter-spacing: 0.05em;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    animation: sg-lobby-glow 4s ease-in-out infinite;
    line-height: 1;
  }
  .sg-lobby-sub {
    font-size: 1rem;
    letter-spacing: 0.35em;
    color: #94A3B8;
    text-transform: uppercase;
    margin-top: -0.25rem;
  }
  .sg-lobby-prompt {
    margin-top: 1.5rem;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.6rem;
    letter-spacing: 0.06em;
    color: #E2E8F0;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.7) 100%);
    padding: 1rem 1.6rem;
    border-radius: 14px;
    border: 2px solid var(--sg-primary, #FFC43D);
    box-shadow: 0 0 40px rgba(255,196,61,0.25), 0 6px 30px rgba(0,0,0,0.5);
    animation: sg-lobby-bounce 2.4s ease-in-out infinite;
    max-width: 100%;
  }
  .sg-lobby-prompt code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.12);
    padding: 0.1em 0.4em;
    border-radius: 6px;
    font-family: ui-monospace, monospace;
    font-size: 1em;
    margin: 0 0.15em;
  }
  .sg-lobby-foot {
    margin-top: 0.75rem;
    font-size: 0.85rem;
    opacity: 0.75;
    letter-spacing: 0.05em;
    max-width: 90%;
  }
  .sg-lobby-foot code {
    color: var(--sg-accent, #3DEFFF);
    background: rgba(61,239,255,0.1);
    padding: 0 0.35em;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
  }
  .sg-lobby-tail-fallback {
    /* Narrow-viewport fallback is now unused — the DOM stage always renders at
       1920px internally regardless of actual viewport size, so the three-column
       layout never collapses. Kept hidden for backward compat with existing
       setLeaderboard() call that still writes its text content. */
    display: none;
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: #94A3B8;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('sg-lobby-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-lobby-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createLobby(mount: HTMLElement): LobbyHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'sg-lobby-wrap';
  el.setAttribute('data-scene-overlay', 'lobby');
  el.innerHTML = `
    <div class="sg-lobby-col-left" data-role="col-left"></div>
    <div class="sg-lobby-col-center">
      <div class="sg-lobby-title">WHAT IS</div>
      <div class="sg-lobby-sub">a game on The Gamer Network</div>
      <div data-testid="lobby-prompt" class="sg-lobby-prompt">
        Streamer: type <code>!start</code> in chat to begin
      </div>
      <div class="sg-lobby-foot">
        Viewers: when the game starts, vote on categories with <code>1</code>-<code>6</code>, answer with <code>!a &lt;answer&gt;</code>
      </div>
      <div class="sg-lobby-tail-fallback" data-role="tail-fallback"></div>
    </div>
    <div class="sg-lobby-col-right" data-role="col-right"></div>
  `;
  mount.appendChild(el);

  const promptEl = el.querySelector<HTMLDivElement>('[data-testid="lobby-prompt"]')!;
  const leftCol = el.querySelector<HTMLDivElement>('[data-role="col-left"]')!;
  const rightCol = el.querySelector<HTMLDivElement>('[data-role="col-right"]')!;
  const tailFallback = el.querySelector<HTMLDivElement>('[data-role="tail-fallback"]')!;

  const lbTop: LobbyLeaderboardHandle = createLobbyLeaderboardTop(leftCol);
  // "How to Play" panel fills the bottom half of the left column. The column
  // is a flex container with `flex: 1 1 0` children, so the two panels split
  // the available vertical space 50/50 regardless of stage height.
  const howTo: LobbyHowToHandle = createLobbyHowTo(leftCol);
  const lbTail: LobbyLeaderboardHandle = createLobbyLeaderboardTail(rightCol);
  // Seed empty state so the panels render even before the first snapshot
  // arrives (covers the connecting → lobby first-paint window).
  lbTop.setRows([]);
  lbTail.setRows([]);

  function setVisible(visible: boolean): void {
    el.style.display = visible ? 'grid' : 'none';
    if (visible) requestAnimationFrame(() => fitText(promptEl, { min: 14, max: 26 }));
  }

  function setPrefix(prefix: string): void {
    promptEl.innerHTML = `Streamer: type <code>${prefix}start</code> in chat to begin`;
    requestAnimationFrame(() => fitText(promptEl, { min: 14, max: 26 }));
  }

  function setLeaderboard(rows: LbRow[]): void {
    lbTop.setRows(rows);
    lbTail.setRows(rows);
    // Narrow-viewport fallback copy. Hidden via media query on wide screens.
    const total = rows.length;
    tailFallback.textContent =
      total > 10 ? `Full standings: ${total} players total` : '';
  }

  return {
    el,
    setVisible,
    setPrefix,
    setLeaderboard,
    dispose() {
      lbTop.dispose();
      howTo.dispose();
      lbTail.dispose();
      el.remove();
    },
  };
}
